import React, { useEffect, useMemo, useRef, useState } from "react";
import "./MultiSelectDropdown.css";


const ChevronIcon = ({ className = "" }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CloseIcon = ({ className = "" }) => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SearchIcon = ({ className = "" }) => (
  <svg
    className={className}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);



const getOptionKey = (opt) => {
  if (opt === null || typeof opt !== "object") return opt;
  return opt.value ?? opt._id ?? opt.id ?? opt.name ?? opt.examYear ?? JSON.stringify(opt);
};

const getOptionLabel = (opt) => {
  if (opt === null || typeof opt !== "object") return String(opt);
  return String(opt.label ?? opt.name ?? opt.examYear ?? opt.value ?? getOptionKey(opt));
};



const MultiSelectDropdown = ({
  options: optionsProp,
  value: valueProp,
  onChange = () => {},
  placeholder = "Select…",
  label,
  disabled = false,
  maxHeight = 240,
}) => {
  // APIs sometimes send null — treat anything that isn't an array as empty
  const options = Array.isArray(optionsProp) ? optionsProp : [];
  const value = Array.isArray(valueProp) ? valueProp : [];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selectedKeys = useMemo(() => value.map(getOptionKey), [value]);

  const selectedOptions = useMemo(
    () => value.map((raw) => ({ raw, key: getOptionKey(raw), label: getOptionLabel(raw) })),
    [value],
  );

  // Options still pickable: not already selected, and matching the search query
  const availableOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .map((raw) => ({ raw, key: getOptionKey(raw), label: getOptionLabel(raw) }))
      .filter((opt) => !selectedKeys.includes(opt.key) && (q === "" || opt.label.toLowerCase().includes(q)));
  }, [options, selectedKeys, query]);

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const addValue = (opt) => {
    onChange([...value, opt.raw]);
    setQuery("");
    inputRef.current?.focus();
  };

  const removeValue = (key, e) => {
    e?.stopPropagation();
    onChange(value.filter((v) => getOptionKey(v) !== key));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && query === "" && selectedOptions.length > 0) {
      // Backspace on an empty search removes the last chip, like most tag inputs
      removeValue(selectedOptions[selectedOptions.length - 1].key);
    } else if (e.key === "Enter") {
      e.preventDefault(); // inside a form (the filter drawer) Enter must pick, not submit
      if (availableOptions.length > 0) addValue(availableOptions[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className={`msd-field ${disabled ? "msd-disabled" : ""}`} ref={wrapRef}>
      {label && <label className="msd-label">{label}</label>}

      <div className={`msd-control ${open ? "msd-control-open" : ""}`} onClick={openList}>
        <div className="msd-chips">
          {selectedOptions.map((opt) => (
            <span className="msd-chip" key={opt.key}>
              {opt.label}
              <button
                type="button"
                className="msd-chip-remove"
                aria-label={`Remove ${opt.label}`}
                onClick={(e) => removeValue(opt.key, e)}
                disabled={disabled}
              >
                <CloseIcon />
              </button>
            </span>
          ))}

          <input
            ref={inputRef}
            className="msd-input"
            type="text"
            value={query}
            disabled={disabled}
            placeholder={selectedOptions.length === 0 ? placeholder : ""}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={openList}
            onKeyDown={handleKeyDown}
          />
        </div>

        <ChevronIcon className={`msd-chevron ${open ? "msd-chevron-open" : ""}`} />
      </div>

      {open && !disabled && (
        <div className="msd-panel" style={{ maxHeight }}>
          <div className="msd-search-row">
            <SearchIcon className="msd-search-icon" />
            <span className="msd-search-hint">
              {query ? `Matching "${query}"` : "Type to filter"}
            </span>
          </div>

          {availableOptions.length === 0 ? (
            <div className="msd-empty">
              {options.length === value.length ? "All options selected" : "No matches"}
            </div>
          ) : (
            <ul className="msd-options">
              {availableOptions.map((opt) => (
                <li key={opt.key} className="msd-option" onClick={() => addValue(opt)}>
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;